import Markdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import {cn} from "../ui/lib/utils";
import React, {useEffect} from "react";
import {toolRenderers, DefaultToolCallRenderer, DefaultToolResponseRenderer} from "./ToolRenderers";
import {Message} from "./GuardianChat";
import { fetchToolMessages, ToolMessagePair } from "../../clients/metoro/guardian";


export function MarkdownRenderer({content}: { content: string }) {
    const [toolCallIdToMessagesMap, setToolCallIdToMessagesMap] = React.useState<Record<string, Message[]>>({});

    useEffect(() => {
        // We want to parse out all "tool_call_id": "some_id" from the content, then fetch the messages for those IDs so we can render them later.
        const toolCallIdRegex = /"tool_call_id":\s*"([^"]+)"/g;
        const matches = content.matchAll(toolCallIdRegex);
        const toolCallIds = Array.from(matches, match => match[1]);
        
        if (toolCallIds.length > 0) {
            fetchToolMessages(toolCallIds).then(response => {
                const map: Record<string, Message[]> = {};
                
                response.tool_messages.forEach(pair => {
                    const messages: Message[] = [];
                    
                    // Add tool call message
                    if (pair.tool_call_message) {
                        messages.push({
                            role: 'assistant' as const,
                            content: '',
                            tool_call_id: pair.tool_call_id,
                            tool_call_function_name: pair.tool_call_message.tool_call_function_name,
                            tool_call_function_args: pair.tool_call_message.tool_call_function_args,
                            tool_call: true
                        });
                    }
                    
                    // Add tool response message
                    if (pair.tool_response) {
                        messages.push({
                            role: 'tool' as const,
                            content: '',
                            // tool_name: pair.tool_call_message?.tool_call_function_name,
                            tool_call_response_id: pair.tool_response.tool_call_response_id,
                            tool_call_response_content: pair.tool_response.tool_call_response_content || pair.tool_response.ai_compressed_tool_call_response_content
                        });
                    }
                    
                    if (messages.length > 0) {
                        map[pair.tool_call_id] = messages;
                    }
                });
                
                setToolCallIdToMessagesMap(map);
            }).catch(error => {
                console.error('Failed to fetch tool messages:', error);
            });
        }
    }, [content]);

    return (
        <div
            className="prose prose-invert max-w-none prose-p:my-4 prose-pre:my-4 prose-headings:my-6 prose-ul:my-4 prose-ol:my-4 prose-blockquote:my-4 prose-table:my-4">
            <Markdown
                rehypePlugins={[rehypeHighlight]}
                remarkPlugins={[remarkGfm]}
                components={{
                    // Override pre and code styling
                    pre: ({children, ...props}) => {
                        // Check if this contains a tool_call code block
                        const codeElement = React.Children.toArray(children).find(
                            child => React.isValidElement(child) && child.type === 'code'
                        );

                        if (codeElement && React.isValidElement(codeElement)) {
                            const className = codeElement.props.className;
                            if (className === 'language-tool_call') {
                                // For tool_call blocks, we don't want the pre wrapper
                                return <>{children}</>;
                            }
                        }

                        return (
                            <pre className="bg-transparent rounded-lg overflow-x-auto">
                                {children}
                            </pre>
                        );
                    },
                    code: ({children, className, node}) => {
                        // Check if this is a tool_call code block
                        const isToolCall = className?.includes('language-tool_call');

                        if (isToolCall && typeof children === 'string') {
                            try {
                                // Parse the JSON content
                                const toolData = JSON.parse(children);

                                // Lets understand whether its a raw tool_call or if it's a tool_call_id
                                if (toolData.tool_call_id) {
                                    // Look up the messages from our pre-fetched map
                                    const messages = toolCallIdToMessagesMap[toolData.tool_call_id];
                                    if (!messages || messages.length === 0) {
                                        return <div className="text-textmedium">Loading tool messages...</div>;
                                    }

                                    const call = messages[0]

                                    const CustomRenderer = call.tool_call_function_name && toolRenderers[call.tool_call_function_name];

                                    return (
                                        <div className="">
                                            {messages.map((message, index) => {
                                                if (message.role === 'assistant' && message.tool_call) {
                                                    // Render tool call
                                                    return (
                                                        <div key={index} className={index > 0 ? "mt-4" : ""}>
                                                            {CustomRenderer ? (
                                                                <CustomRenderer message={message} isToolCall={true} />
                                                            ) : (
                                                                <DefaultToolCallRenderer message={message} />
                                                            )}
                                                        </div>
                                                    );
                                                } else if (message.role === 'tool') {
                                                    // Render tool response
                                                    return (
                                                        <div key={index}>
                                                            {CustomRenderer ? (
                                                                <CustomRenderer message={message} isToolCall={false} />
                                                            ) : (
                                                                <DefaultToolResponseRenderer message={message} />
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    );
                                }

                                // Create the tool call message
                                const toolCallMessage: Message = {
                                    role: 'assistant',
                                    content: '',
                                    tool_call_function_name: toolData.tool,
                                    tool_call_function_args: JSON.stringify(toolData.args || {}),
                                    tool_call: true
                                };

                                // Create the tool response message
                                const toolResponseMessage: Message = {
                                    role: 'tool',
                                    content: '',
                                    tool_call_response_content: JSON.stringify(toolData.response || {}),
                                    tool_name: toolData.tool
                                };

                                // Check if there's a custom renderer for this tool
                                const CustomRenderer = toolData.tool && toolRenderers[toolData.tool];

                                return (
                                    <div className="">
                                        {/* Render the tool call */}
                                        {CustomRenderer && (
                                            <div className={"mb-4"}>
                                                <CustomRenderer message={toolCallMessage} isToolCall={true}/>
                                            </div>
                                        )}


                                        {/* Render the tool response */}
                                        {toolData.response && (
                                            CustomRenderer && (
                                                <CustomRenderer message={toolResponseMessage} isToolCall={false}/>
                                            )
                                        )}
                                    </div>
                                );
                            } catch (e) {
                                // If parsing fails, render as normal code block
                                console.error('Failed to parse tool_call JSON:', e);
                            }
                        }

                        // Normal code block rendering
                        return className ? (
                            <div className="bg-[#1D2739] flex grow shrink p-4">
                                <code className={cn("bg-[#1D2739] text-sm", className)}>
                                    {children}
                                </code>
                            </div>
                        ) : (
                            <code className="bg-[#1D2739] rounded px-1.5 py-0.5 text-sm">
                                {children}
                            </code>
                        )
                    },
                    p: ({children}) => (
                        <p className="my-4 leading-7">
                            {children}
                        </p>
                    ),
                    // Add proper heading styles
                    h1: ({children}) => (
                        <h1 className="text-2xl font-bold my-6 text-textlight">
                            {children}
                        </h1>
                    ),
                    h2: ({children}) => (
                        <h2 className="text-xl font-bold my-5 text-textlight">
                            {children}
                        </h2>
                    ),
                    h3: ({children}) => (
                        <h3 className="text-lg font-bold my-4 text-textlight">
                            {children}
                        </h3>
                    ),
                    h4: ({children}) => (
                        <h4 className="text-base font-bold my-4 text-textlight">
                            {children}
                        </h4>
                    ),
                    h5: ({children}) => (
                        <h5 className="text-sm font-bold my-3 text-textlight">
                            {children}
                        </h5>
                    ),
                    h6: ({children}) => (
                        <h6 className="text-sm font-bold my-3 text-textlight">
                            {children}
                        </h6>
                    ),
                    // Add proper list styles
                    ul: ({children}) => (
                        <ul className="list-disc pl-6 my-4 space-y-2">
                            {children}
                        </ul>
                    ),
                    ol: ({children}) => (
                        <ol className="list-decimal pl-6 my-4 space-y-2">
                            {children}
                        </ol>
                    ),
                    li: ({children}) => (
                        <li className="leading-7">
                            {children}
                        </li>
                    ),
                    table: ({children}) => (
                        <div className="my-4 overflow-x-auto">
                            <table className="border-collapse">
                                {children}
                            </table>
                        </div>
                    ),
                    th: ({children}) => (
                        <th className="border border-[#1D2739] px-4 py-2 bg-[#131B2E]">
                            {children}
                        </th>
                    ),
                    td: ({children}) => (
                        <td className="border border-[#1D2739] px-4 py-2">
                            {children}
                        </td>
                    ),
                }}
            >
                {content}
            </Markdown>
        </div>
    );
}
